import React, { useState, useEffect } from "react";
import { useOutletContext } from "react-router-dom";
import { AlertTriangle, RefreshCw, CheckCircle, RotateCcw, Eye, XCircle } from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL;

export function QuarantinePage() {
  const { adminKey } = useOutletContext();
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [actionModal, setActionModal] = useState(null);
  const [actionReason, setActionReason] = useState("");

  useEffect(() => {
    fetchQuarantine();
  }, [adminKey]);

  const fetchQuarantine = async () => {
    try {
      setLoading(true);
      const res = await fetch(`${API_URL}/api/admin/quarantine?limit=100`, {
        headers: { "X-Admin-Key": adminKey }
      });
      
      if (res.ok) {
        const data = await res.json();
        setEvents(data.events || []);
      }
    } catch (err) {
      console.error("Failed to fetch quarantine:", err);
    } finally {
      setLoading(false);
    }
  };

  const fetchEventDetail = async (eventId) => {
    try {
      const res = await fetch(`${API_URL}/api/admin/failed-operations/${eventId}`, {
        headers: { "X-Admin-Key": adminKey }
      });
      
      if (res.ok) {
        setSelectedEvent(await res.json());
      }
    } catch (err) {
      console.error("Failed to fetch event detail:", err);
    }
  };

  const handleAction = async (action, eventId) => {
    if (!actionReason.trim()) {
      alert("Reason is required for audit trail");
      return;
    }

    try {
      const endpoint = action === "resolve" 
        ? `/api/admin/failed-operations/${eventId}/resolve`
        : `/api/admin/failed-operations/${eventId}/retry`;

      const res = await fetch(`${API_URL}${endpoint}`, {
        method: "POST",
        headers: {
          "X-Admin-Key": adminKey,
          "Content-Type": "application/json"
        },
        body: JSON.stringify({ reason: actionReason })
      });

      if (res.ok) {
        setActionModal(null);
        setActionReason("");
        fetchQuarantine();
        alert(`Event ${action}d successfully`);
      } else {
        const err = await res.json();
        alert(`Error: ${err.detail || "Failed to perform action"}`);
      }
    } catch (err) {
      alert(`Error: ${err.message}`);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">Quarantine Queue</h1>
          <p className="text-gray-500 text-sm mt-1">Failed webhook events that need manual review</p>
        </div>
        <button 
          onClick={fetchQuarantine}
          className="flex items-center gap-2 px-4 py-2 bg-gray-50 text-gray-800 rounded-lg hover:bg-gray-200"
        >
          <RefreshCw className="w-4 h-4" /> Refresh
        </button>
      </div>

      {/* Stats Banner */}
      <div className={`rounded-xl p-4 border ${events.length === 0 ? "bg-emerald-900/30 border-green-200" : "bg-orange-900/30 border-orange-700"}`}>
        <div className="flex items-center gap-4">
          {events.length === 0 ? (
            <CheckCircle className="w-8 h-8 text-green-600" />
          ) : (
            <AlertTriangle className="w-8 h-8 text-orange-400" />
          )}
          <div>
            <h2 className="text-xl font-bold text-gray-800">
              {events.length === 0 ? "All Clear!" : `${events.length} Events Quarantined`}
            </h2>
            <p className="text-gray-600 text-sm">
              {events.length === 0 
                ? "No failed webhook events requiring attention" 
                : "Review and resolve these events to ensure data integrity"}
            </p>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Events List */}
        <div className="lg:col-span-2 bg-white rounded-xl border border-gray-200 overflow-hidden">
          <div className="p-4 border-b border-gray-200">
            <h2 className="text-gray-800 font-semibold">Quarantined Events</h2>
          </div>
          
          {loading ? (
            <div className="p-8 text-center text-gray-500">Loading...</div>
          ) : events.length === 0 ? (
            <div className="p-8 text-center text-gray-500">
              <CheckCircle className="w-12 h-12 mx-auto mb-3 opacity-50" />
              <p>No quarantined events</p>
            </div>
          ) : (
            <div className="overflow-x-auto max-h-[500px] overflow-y-auto">
              <table className="w-full">
                <thead className="bg-gray-50/50 sticky top-0">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Event</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Type</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Error</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-700">
                  {events.map((event, idx) => (
                    <tr 
                      key={event.id || idx} 
                      className={`hover:bg-gray-50/30 cursor-pointer ${selectedEvent?.id === event.id ? "bg-gray-50/50" : ""}`}
                      onClick={() => fetchEventDetail(event.id)}
                    >
                      <td className="px-4 py-3">
                        <p className="text-gray-800 font-mono text-sm">{event.id}</p>
                        <p className="text-gray-500 text-xs">{new Date(event.created_at).toLocaleString()}</p>
                      </td>
                      <td className="px-4 py-3">
                        <span className="px-2 py-1 rounded bg-gray-50 text-gray-600 text-xs">
                          {event.type}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-red-600 text-sm max-w-xs truncate">
                        {event.error || "Unknown error"}
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex gap-2">
                          <button 
                            onClick={(e) => { e.stopPropagation(); setActionModal({ type: "resolve", eventId: event.id }); }}
                            className="p-2 hover:bg-emerald-600 rounded-lg text-green-600 hover:text-gray-800"
                            title="Mark Resolved"
                          >
                            <CheckCircle className="w-4 h-4" />
                          </button>
                          <button 
                            onClick={(e) => { e.stopPropagation(); setActionModal({ type: "retry", eventId: event.id }); }}
                            className="p-2 hover:bg-blue-600 rounded-lg text-blue-600 hover:text-gray-800"
                            title="Retry"
                          >
                            <RotateCcw className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Event Detail Panel */}
        <div className="bg-white rounded-xl border border-gray-200 p-4">
          {selectedEvent ? (
            <div className="space-y-4">
              <h3 className="text-gray-800 font-semibold">Event Details</h3>
              
              <div className="space-y-3">
                <DetailRow label="Event ID" value={selectedEvent.id} mono />
                <DetailRow label="Type" value={selectedEvent.type} />
                <DetailRow label="Status" value={selectedEvent.status} />
                <DetailRow label="Retry Count" value={selectedEvent.retry_count || 0} />
                <DetailRow label="Created" value={new Date(selectedEvent.created_at).toLocaleString()} />
              </div>

              {selectedEvent.error && (
                <div className="bg-red-900/30 border border-red-200 rounded-lg p-3">
                  <p className="text-red-600 text-sm font-semibold mb-1">Error</p>
                  <p className="text-red-300 text-sm">{selectedEvent.error}</p>
                </div>
              )}

              {selectedEvent.payload && (
                <div className="bg-gray-50/50 rounded-lg p-3">
                  <p className="text-gray-600 text-sm font-semibold mb-2">Payload</p>
                  <pre className="text-gray-500 text-xs overflow-auto max-h-40">
                    {JSON.stringify(selectedEvent.payload, null, 2)}
                  </pre>
                </div>
              )}

              <div className="flex gap-2 pt-4 border-t border-gray-200">
                <button 
                  onClick={() => setActionModal({ type: "resolve", eventId: selectedEvent.id })}
                  className="flex-1 flex items-center justify-center gap-2 py-2 bg-emerald-600 text-gray-800 rounded-lg hover:bg-emerald-700"
                >
                  <CheckCircle className="w-4 h-4" /> Resolve
                </button>
                <button 
                  onClick={() => setActionModal({ type: "retry", eventId: selectedEvent.id })}
                  className="flex-1 flex items-center justify-center gap-2 py-2 bg-blue-600 text-gray-800 rounded-lg hover:bg-blue-700"
                >
                  <RotateCcw className="w-4 h-4" /> Retry
                </button>
              </div>
            </div>
          ) : (
            <div className="text-center text-gray-500 py-8">
              <Eye className="w-12 h-12 mx-auto mb-3 opacity-50" />
              <p>Select an event to view details</p>
            </div>
          )}
        </div>
      </div>

      {/* Action Modal */}
      {actionModal && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-md w-full p-6 border border-gray-200">
            <h3 className="text-xl font-bold text-gray-800 mb-4 capitalize">
              {actionModal.type} Event
            </h3>
            
            <div className="space-y-4">
              <div>
                <label className="block text-gray-600 text-sm mb-1">Reason (required for audit)</label>
                <textarea
                  value={actionReason}
                  onChange={(e) => setActionReason(e.target.value)}
                  placeholder={actionModal.type === "resolve" 
                    ? "e.g., Manually processed the data..." 
                    : "e.g., Fixed agent mapping, retrying..."
                  }
                  className="w-full px-3 py-2 bg-gray-50 border border-gray-300 rounded-lg text-gray-800 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-red-500"
                  rows={3}
                />
              </div>
              
              <div className="flex gap-3 pt-4">
                <button 
                  onClick={() => { setActionModal(null); setActionReason(""); }}
                  className="flex-1 py-2 bg-gray-50 text-gray-800 rounded-lg hover:bg-gray-200"
                >
                  Cancel
                </button>
                <button 
                  onClick={() => handleAction(actionModal.type, actionModal.eventId)}
                  className={`flex-1 py-2 text-gray-800 rounded-lg font-semibold ${
                    actionModal.type === "resolve" ? "bg-emerald-600 hover:bg-emerald-700" : "bg-blue-600 hover:bg-blue-700"
                  }`}
                >
                  Confirm {actionModal.type}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function DetailRow({ label, value, mono }) {
  return (
    <div className="flex justify-between">
      <span className="text-gray-500 text-sm">{label}</span>
      <span className={`text-gray-800 text-sm ${mono ? "font-mono" : ""}`}>{value}</span>
    </div>
  );
}

export default QuarantinePage;
